import React from 'react';

import {
  ApiOutlined,
  CarryOutOutlined,
  ControlOutlined,
  DollarCircleOutlined,
  FilterOutlined,
  LineChartOutlined,
  MessageOutlined,
  MonitorOutlined,
  ProfileOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShoppingOutlined,
  TableOutlined,
} from '@ant-design/icons';
import {
  IntegratedErps,
  TenantFeatureFlag,
  TenantIntegrationStatus,
  TenantProductName,
  RecurrencyRole,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantDTO } from '@recurrency/core-api-schema/dist/tenants/tenantDTO';
import { TenantUserErpRoleDTO } from '@recurrency/core-api-schema/dist/tenants/tenantUserDTO';

import { recurrencyRolesToShowSettingsPage } from 'pages/admin/SettingsPage';
import { hasChatWithDataAccess } from 'pages/chat/access';

import { ExtendedUser } from 'hooks/useGlobalApp';

import {
  arePermissionsEnabled,
  hasUserPermission,
  isAtLeastOneOfRecurrencyRoles,
  isPurchaserRole,
  shouldShowDailyWorkflow,
  shouldShowFeatureFlag,
  shouldShowProduct,
} from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { getTenantSetting, isHubAndSpokeEnabled } from 'utils/tenantSettings';

import { NavModule, NavSubModule, NavMode } from './types';

export function filterNav<Type extends NavModule>(
  array: Type[],
  activeTenant: TenantDTO,
  activeUser: ExtendedUser,
  activeErpRole: Maybe<TenantUserErpRoleDTO>,
) {
  return filterEmptyModules(
    array.map((navModule) => ({
      ...navModule,
      subModules: applyAllSubmoduleFilters(navModule.subModules, activeTenant, activeUser, activeErpRole),
    })),
  );
}

function applyAllSubmoduleFilters(
  subModules: NavSubModule[],
  activeTenant: TenantDTO,
  activeUser: ExtendedUser,
  activeErpRole: Maybe<TenantUserErpRoleDTO>,
) {
  let f: NavSubModule[];
  f = filterByNavMode(subModules, activeTenant, activeUser);
  f = filterByActiveErpRole(f, activeErpRole);
  f = filterByRecurrencyRoles(f, activeTenant);
  f = filterByPermissions(f, activeTenant);
  f = filterByErpType(f, activeTenant);
  f = filterByProducts(f, activeTenant);
  f = filterByFeatureFlag(f, activeTenant, activeUser);
  f = filterByTenantIntegrationStatus(f, activeTenant);
  f = filterByTenantSetting(f);
  f = filterByCustom(f, activeTenant, activeUser);
  return f;
}

function filterByNavMode(subModules: NavSubModule[], activeTenant: TenantDTO, activeUser: ExtendedUser) {
  let returnArray = subModules;

  // probably want to check if tenantAdmin here
  const isInSetupMode = !activeTenant.isActive;
  if (isInSetupMode) {
    returnArray = returnArray.filter((item) => item.navMode === NavMode.Setup || item.navMode === NavMode.Internal);
  } else {
    returnArray = returnArray.filter((item) => item.navMode !== NavMode.Setup);
  }

  const { isRecurrencyAdmin } = activeUser;
  if (!isRecurrencyAdmin) {
    returnArray = returnArray.filter((item) => item.navMode !== NavMode.Internal);
  }

  return returnArray;
}

function filterByActiveErpRole(subModules: NavSubModule[], activeErpRole: Maybe<TenantUserErpRoleDTO>) {
  return subModules.filter((subModule) =>
    subModule.isEnabledForErpRole !== undefined ? subModule.isEnabledForErpRole(activeErpRole) : true,
  );
}

function filterByRecurrencyRoles(subModules: NavSubModule[], activeTenant: TenantDTO) {
  // once a tenant is on permissions, an entry's permission replaces its role; role-only entries keep their role gate
  return subModules.filter((subModule) =>
    subModule.recurrencyRoles !== undefined && !(subModule.permissions !== undefined && arePermissionsEnabled())
      ? isAtLeastOneOfRecurrencyRoles(activeTenant, subModule.recurrencyRoles)
      : true,
  );
}

function filterByPermissions(subModules: NavSubModule[], activeTenant: TenantDTO) {
  return subModules.filter((subModule) =>
    subModule.permissions !== undefined
      ? subModule.permissions.some((permission) => hasUserPermission(activeTenant, permission))
      : true,
  );
}

function filterByErpType(subModules: NavSubModule[], activeTenant: TenantDTO) {
  return subModules.filter((subModule) => (subModule.erpType ? subModule.erpType === activeTenant.erpType : true));
}

function filterByProducts(subModules: NavSubModule[], activeTenant: TenantDTO) {
  return subModules.filter(
    (submodule) => submodule.products?.some((product) => shouldShowProduct(activeTenant, product)) ?? true,
  );
}

function filterByFeatureFlag(subModules: NavSubModule[], activeTenant: TenantDTO, activeUser: ExtendedUser) {
  return subModules.filter((submodule) =>
    submodule.featureFlag ? shouldShowFeatureFlag(activeTenant, activeUser, submodule.featureFlag) : true,
  );
}

function filterEmptyModules(modules: NavModule[]) {
  return modules.filter((module) => module.subModules.length > 0);
}

function filterByTenantIntegrationStatus(subModules: NavSubModule[], activeTenant: TenantDTO) {
  const integrationStatuses = activeTenant.integrations.map((i) => i.status);
  return subModules.filter((item) =>
    item.tenantIntegrationStatuses ? item.tenantIntegrationStatuses.some((r) => integrationStatuses.includes(r)) : true,
  );
}

function filterByTenantSetting(subModules: NavSubModule[]) {
  return subModules.filter((submodule) => (submodule.tenantSetting ? getTenantSetting(submodule.tenantSetting) : true));
}

function filterByCustom(subModules: NavSubModule[], activeTenant: TenantDTO, activeUser: ExtendedUser) {
  return subModules.filter((submodule) =>
    submodule.customFilter ? submodule.customFilter(activeTenant, activeUser) : true,
  );
}

const IconWithStyle = (Icon: React.ElementType) => <Icon height={18} width={24} flex={1} />;

export const upperNavModules: NavModule[] = [
  {
    slug: 'demand-planning',
    label: 'Demand Planning',
    selectedIcon: IconWithStyle(TableOutlined),
    unselectedIcon: IconWithStyle(TableOutlined),
    subModules: [
      {
        slug: 'dashboard',
        entitySlug: 'dashboard',
        label: 'Dashboard',
        products: [TenantProductName.DfpaMissionControl],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.demandPlanning.dashboard(),
        permissions: [TenantUserPermission.AccessPlanningDashboardView],
      },
      {
        slug: 'daily-workflow',
        label: 'Daily Workflow',
        products: [TenantProductName.DfpaDemandPlanning],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.demandPlanning.dailyWorkflow(),
        permissions: [TenantUserPermission.AccessPlanningDailyWorkflowView],
        customFilter: (tenant, _user) => shouldShowDailyWorkflow(tenant),
      },
      {
        slug: 'forecasting',
        label: 'Forecasting',
        products: [TenantProductName.DfpaDemandPlanning],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.demandPlanning.forecasting(),
        permissions: [TenantUserPermission.AccessPlanningForecastingView],
      },
      {
        slug: 'planning',
        label: 'Planning',
        products: [TenantProductName.DfpaDemandPlanning],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.demandPlanning.planning(),
        permissions: [TenantUserPermission.AccessPlanningPlanningView],
      },
    ],
  },
  {
    slug: 'purchasing',
    label: 'Purchasing',
    selectedIcon: IconWithStyle(ShoppingOutlined),
    unselectedIcon: IconWithStyle(ShoppingOutlined),
    subModules: [
      {
        slug: 'purchase-targets',
        entitySlug: 'purchase-targets',
        label: 'Purchase Targets',
        products: [TenantProductName.DfpaPurchasingAutomation],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.purchaseTargets(),
        permissions: [TenantUserPermission.AccessPurchasingPurchaseTargetsView],
      },
      {
        slug: 'transfer-replenishment',
        entitySlug: 'transfer-replenishment',
        label: 'Transfer Replenishment',
        products: [TenantProductName.DfpaPurchasingAutomation],
        erpType: IntegratedErps.P21,
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.transferReplenishment(),
        permissions: [TenantUserPermission.AccessPurchasingTransferReplenishmentView],
        customFilter: (_tenant, _user) => isHubAndSpokeEnabled(),
      },
      {
        slug: 'purchase-groups',
        entitySlug: 'purchase-groups',
        label: 'Purchase Groups',
        products: [TenantProductName.DfpaPurchasingAutomation],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.purchaseGroups(),
        permissions: [TenantUserPermission.AccessPurchasingPurchaseGroupsView],
      },
      {
        slug: 'purchase-orders',
        entitySlug: 'purchase-order',
        label: 'Purchase Orders',
        products: [TenantProductName.DfpaSearchAndLookup],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.purchaseOrderList(),
        permissions: [TenantUserPermission.AccessPurchasingPurchaseOrdersView],
      },
      {
        slug: 'transfer-orders',
        entitySlug: 'transfer-orders',
        label: 'Transfer Orders',
        products: [TenantProductName.DfpaPurchasingAutomation],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.transferOrderList(),
        permissions: [TenantUserPermission.AccessPurchasingTransferOrdersView],
      },
      {
        slug: 'purchase-status',
        entitySlug: 'purchase-status',
        label: 'Purchase Status',
        products: [TenantProductName.DfpaPurchasingAutomation],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.purchaseStatus(),
        permissions: [TenantUserPermission.AccessPurchasingPurchaseStatusView],
      },
      {
        slug: 'vendors',
        entitySlug: 'vendor',
        label: 'Vendors',
        products: [TenantProductName.DfpaSearchAndLookup],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.vendorList(),
        permissions: [TenantUserPermission.AccessPurchasingVendorsView],
      },
      {
        slug: 'items',
        entitySlug: 'item',
        label: 'Inventory',
        products: [TenantProductName.DfpaSearchAndLookup],
        isEnabledForErpRole: isPurchaserRole,
        routeFn: () => routes.purchasing.itemList(),
        permissions: [TenantUserPermission.AccessPurchasingInventoryView],
      },
    ],
  },
  {
    slug: 'sales',
    label: 'Sales',
    selectedIcon: IconWithStyle(FilterOutlined),
    unselectedIcon: IconWithStyle(FilterOutlined),
    subModules: [
      {
        slug: 'dashboard',
        label: 'Dashboard',
        products: [TenantProductName.SalesMissionControl],
        routeFn: () => routes.sales.dashboard(),
        permissions: [TenantUserPermission.AccessSalesDashboardView],
      },
      {
        slug: 'customers',
        entitySlug: 'customer',
        label: 'Customers',
        products: [TenantProductName.SalesSearchAndLookup],
        routeFn: () => routes.sales.customerList(),
        permissions: [TenantUserPermission.AccessSalesCustomersView],
      },
      {
        slug: 'items',
        entitySlug: 'item',
        label: 'Inventory',
        products: [TenantProductName.SalesSearchAndLookup],
        routeFn: () => routes.sales.itemList(),
        permissions: [TenantUserPermission.AccessPurchasingInventoryView],
      },
      {
        slug: 'pricing',
        label: 'Pricing',
        erpType: IntegratedErps.P21,
        products: [TenantProductName.SalesCsrFrontCounter],
        routeFn: () => routes.sales.pricing(),
      },
    ],
  },
  {
    slug: 'orders',
    label: 'Orders',
    selectedIcon: IconWithStyle(ProfileOutlined),
    unselectedIcon: IconWithStyle(ProfileOutlined),
    subModules: [
      {
        slug: 'quote-drafts',
        entitySlug: 'quote-drafts',
        label: 'Draft Quotes',
        products: [TenantProductName.SalesQuoting],
        erpType: IntegratedErps.P21,
        routeFn: () => routes.orders.draftQuoteList(),
        permissions: [TenantUserPermission.AccessOrdersQuotesView],
      },
      {
        slug: 'quotes',
        entitySlug: 'quote',
        label: 'Quotes',
        products: [TenantProductName.SalesQuoting],
        routeFn: () => routes.orders.quoteList(),
        permissions: [TenantUserPermission.AccessOrdersQuotesView],
      },
      {
        slug: 'orders',
        entitySlug: 'order',
        label: 'Orders',
        products: [TenantProductName.SalesSearchAndLookup, TenantProductName.DfpaSearchAndLookup],
        routeFn: () => routes.orders.orderList(),
        permissions: [TenantUserPermission.AccessOrdersOrdersView],
      },
    ],
  },
  {
    slug: 'payments',
    label: 'Payments',
    selectedIcon: IconWithStyle(DollarCircleOutlined),
    unselectedIcon: IconWithStyle(DollarCircleOutlined),
    subModules: [
      {
        slug: 'payouts',
        entitySlug: 'payout',
        label: 'Payouts',
        products: [TenantProductName.Payments],
        routeFn: () => routes.payments.payouts(),
      },
      {
        slug: 'payments',
        entitySlug: 'payments',
        label: 'Payments',
        products: [TenantProductName.Payments],
        routeFn: () => routes.payments.payments(),
      },
      {
        slug: 'open-invoices',
        entitySlug: 'open-invoices',
        label: 'Open Invoices',
        products: [TenantProductName.Payments],
        routeFn: () => routes.payments.openInvoices(),
      },
      {
        slug: 'disputes',
        entitySlug: 'disputes',
        label: 'Disputes',
        products: [TenantProductName.Payments],
        routeFn: () => routes.payments.disputes(),
      },
    ],
  },
  {
    slug: 'reporting',
    label: 'Sales Reporting',
    selectedIcon: IconWithStyle(LineChartOutlined),
    unselectedIcon: IconWithStyle(LineChartOutlined),
    subModules: [
      {
        slug: 'explorer',
        entitySlug: 'report',
        label: 'Explorer',
        products: [TenantProductName.SalesReporting],
        routeFn: () => routes.reporting.explorer(),
        permissions: [TenantUserPermission.AccessReportingReportingView],
      },
      {
        slug: 'reports',
        label: 'Reports',
        products: [TenantProductName.SalesReporting],
        routeFn: () => routes.reporting.reports(),
        permissions: [TenantUserPermission.AccessReportingReportingView],
      },
    ],
  },
  {
    slug: 'tasks',
    label: 'Tasks',
    selectedIcon: IconWithStyle(CarryOutOutlined),
    unselectedIcon: IconWithStyle(CarryOutOutlined),
    subModules: [
      {
        slug: 'tasks',
        entitySlug: 'task',
        label: 'Tasks',
        products: [TenantProductName.SalesCrm],
        routeFn: () => routes.tasks.taskList(),
        permissions: [TenantUserPermission.AccessTasksTasksView],
      },
    ],
  },
  {
    slug: 'setup',
    label: 'Setup',
    selectedIcon: IconWithStyle(ApiOutlined),
    unselectedIcon: IconWithStyle(ApiOutlined),
    subModules: [
      {
        slug: 'integration',
        label: 'Database Connection',
        navMode: NavMode.Setup,
        routeFn: () => routes.setup.integration(),
      },
    ],
  },
  {
    slug: 'rule-builder',
    label: 'Rule Builder',
    selectedIcon: IconWithStyle(SafetyCertificateOutlined),
    unselectedIcon: IconWithStyle(SafetyCertificateOutlined),
    subModules: [
      {
        slug: 'rule-management',
        label: 'Rule Management',
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        routeFn: () => routes.ruleBuilder.ruleManagement(),
        permissions: [TenantUserPermission.AccessRuleBuilderRulesView],
        products: [TenantProductName.DfpaDemandPlanning],
      },
      {
        slug: 'rule-templates',
        label: 'Rule Templates',
        routeFn: () => routes.ruleBuilder.ruleTemplates(),
        products: [TenantProductName.DfpaDemandPlanning],
        navMode: NavMode.Internal, // Only visible to internal Recurrency users
      },
    ],
  },

  {
    slug: 'chat',
    label: 'Cue',
    selectedIcon: IconWithStyle(MessageOutlined),
    unselectedIcon: IconWithStyle(MessageOutlined),
    subModules: [
      {
        slug: 'data',
        label: 'Cue',
        routeFn: () => routes.chat.chatWithData(),
        permissions: [TenantUserPermission.AccessChatDataView],
        customFilter: (tenant, user) => hasChatWithDataAccess(tenant, user),
      },
    ],
  },
  {
    slug: 'monitoring',
    label: 'Monitoring',
    selectedIcon: IconWithStyle(MonitorOutlined),
    unselectedIcon: IconWithStyle(MonitorOutlined),
    subModules: [
      {
        slug: 'centralized-audit-logs',
        label: 'Audit Logs',
        routeFn: () => routes.monitoring.centralizedAuditLogs(),
        permissions: [TenantUserPermission.AccessSettingsMonitoringView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
      },
      {
        slug: 'export-jobs',
        label: 'Export Jobs',
        routeFn: () => routes.monitoring.exportJobs(),
        permissions: [TenantUserPermission.AccessSettingsMonitoringView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        featureFlag: TenantFeatureFlag.FeatureExportsStatusPage,
      },
      {
        slug: 'sync-status',
        label: 'Sync Status',
        routeFn: () => routes.monitoring.syncStatus(),
        permissions: [TenantUserPermission.AccessSettingsMonitoringView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        customFilter: (tenant, _user) => tenant.erpType !== IntegratedErps.NETSUITE,
      },
      {
        slug: 'mcp-sessions',
        label: 'MCP Sessions',
        routeFn: () => routes.monitoring.mcpSessions(),
        permissions: [TenantUserPermission.AccessSettingsMcpSessionsView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        products: [TenantProductName.Mcp],
      },
    ],
  },
  {
    slug: 'settings',
    label: 'Settings',
    selectedIcon: IconWithStyle(SettingOutlined),
    unselectedIcon: IconWithStyle(SettingOutlined),
    subModules: [
      {
        slug: 'settings',
        label: 'Settings',
        routeFn: () => routes.settings.settings(),
        permissions: [TenantUserPermission.AccessSettingsGeneralView],
        recurrencyRoles: recurrencyRolesToShowSettingsPage,
        tenantIntegrationStatuses: [
          TenantIntegrationStatus.Onboarding,
          TenantIntegrationStatus.Active,
          TenantIntegrationStatus.Paused,
        ],
      },
      {
        slug: 'users',
        label: 'Users',
        routeFn: () => routes.settings.users(),
        permissions: [TenantUserPermission.AccessSettingsUsersView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
      },
      {
        slug: 'replenishment-paths',
        entitySlug: 'replenishment-paths',
        label: 'Replenishment Paths',
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        routeFn: () => routes.settings.replenishmentPaths(),
        permissions: [TenantUserPermission.AccessSettingsGeneralView],
      },
      {
        slug: 'location-settings',
        label: 'Location Settings',
        routeFn: () => routes.settings.locationSettings(),
        permissions: [TenantUserPermission.AccessSettingsGeneralView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        erpType: IntegratedErps.P21,
      },
      {
        // Legacy settings, sales only and only visible to internal Recurrency users. To be migrated to the new Settings page
        slug: 'legacy-sales-settings',
        label: 'Legacy Sales Settings',
        navMode: NavMode.Internal,
        routeFn: () => routes.settings.legacySalesSettings(),
        tenantIntegrationStatuses: [TenantIntegrationStatus.Active],
      },
      {
        slug: 'update-credentials',
        label: 'Update Credentials',
        routeFn: () => routes.settings.updateCredentials(),
        permissions: [TenantUserPermission.AccessSettingsGeneralView],
        recurrencyRoles: [RecurrencyRole.TenantAdmin],
        customFilter: (tenant, _user) => tenant.erpType !== IntegratedErps.ECLIPSE,
      },
    ],
  },
];

export const lowerNavModules: NavModule[] = [
  {
    slug: 'internal',
    label: 'Recurrency Internal',
    selectedIcon: IconWithStyle(ControlOutlined),
    unselectedIcon: IconWithStyle(ControlOutlined),
    subModules: [
      {
        slug: 'users',
        entitySlug: 'user',
        label: 'Users',
        navMode: NavMode.Internal,
        routeFn: () => routes.internal.userList(),
      },
      {
        slug: 'tenants',
        entitySlug: 'tenant',
        label: 'Tenants',
        navMode: NavMode.Internal,
        routeFn: () => routes.internal.tenantList(),
      },
    ],
  },
];

export const navModules = upperNavModules.concat(lowerNavModules);
